import { isEqual } from "lodash";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { SplitWorkspaces } from "@zapier/experiment-toolkit/src/types";
import { SPLIT_WORKSPACES } from "@zapier/experiment-toolkit/src/constants";

export type Treatments = Record<string, string | null>;

export type ExperimentContextType = {
  treatments: Treatments;
  setTreatments: Dispatch<SetStateAction<Treatments>>;
  splitWorkspace: SplitWorkspaces;
} | null;

export type ExperimentProviderProps = {
  children: ReactNode;
  initialTreatments: Treatments;
  splitWorkspace: SplitWorkspaces;
};

export const ExperimentContext = createContext<ExperimentContextType>(null);

/**
 * A Global Provider to share the default or server side loaded treatments across the app.
 * Wrap the entire app or the part of the app that needs access to default treatments retrieved from server.
 * Use `getServerTreatment` to get the treatment for a given split and provide it to ExperimentProvider.
 */
export function ExperimentProvider({
  children,
  initialTreatments = {},
  splitWorkspace = SPLIT_WORKSPACES.revenue,
}: ExperimentProviderProps) {
  const [treatments, _setTreatments] = useState<Treatments>({
    ...initialTreatments,
  });

  const prevTreatmentsRef = useRef<Treatments>(treatments);

  // prevents re-renders when treatments haven't changed
  const setTreatments: Dispatch<SetStateAction<Treatments>> = useCallback(
    (action) => {
      _setTreatments((prevTreatments) => {
        let newTreatments;
        if (typeof action === "function") {
          newTreatments = action(prevTreatments);
        } else {
          newTreatments = action;
        }

        // Only update treatments if they have changed
        if (!isEqual(newTreatments, prevTreatmentsRef.current)) {
          prevTreatmentsRef.current = newTreatments;
          return newTreatments;
        }

        // If treatments haven't changed, return the previous treatments to prevent a re-render
        return prevTreatments;
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      treatments,
      setTreatments,
      splitWorkspace,
    }),
    [setTreatments, splitWorkspace, treatments]
  );

  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
}
